import * as Sentry from '@sentry/react';

/**
 * Detecta se o usuário está no Instagram WebView (navegador interno do app)
 */
export function isInstagramWebView(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  return /instagram|fban|fbav/.test(ua);
}

/**
 * Detecta se o usuário está no Facebook WebView
 */
export function isFacebookWebView(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  return /fban|fbav|facebook/.test(ua) && !/instagram/.test(ua);
}

/**
 * Detecta Android WebView genérico (app nativo embutindo o browser): o UA marca "; wv)".
 * O Chrome normal do Android NÃO tem esse token.
 */
export function isAndroidWebView(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent.toLowerCase();
  return /android/.test(ua) && /;\s*wv\)/.test(ua);
}

/**
 * Retorna o tipo de navegador interno (in-app) ou null se for navegador normal.
 * OAuth (Google/Microsoft) costuma falhar nesses — por isso o aviso "abrir no navegador".
 */
export function getInAppBrowserType(): string | null {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent.toLowerCase();
  if (/instagram/.test(ua)) return 'instagram';
  if (/fban|fbav|facebook/.test(ua)) return 'facebook';
  if (/line\//.test(ua)) return 'line';
  if (/twitter/.test(ua)) return 'twitter';
  if (/snapchat/.test(ua)) return 'snapchat';
  if (/musical_ly|bytedancewebview|tiktok/.test(ua)) return 'tiktok';
  if (/kakaotalk/.test(ua)) return 'kakaotalk';
  if (/pinterest/.test(ua)) return 'pinterest';
  if (isAndroidWebView()) return 'android-webview';
  return null;
}

/**
 * Detecta se está em qualquer navegador interno (Instagram, Facebook, Android WebView, etc).
 */
export function isInAppBrowser(): boolean {
  return getInAppBrowserType() !== null;
}

/**
 * Log separado (message próprio "webview_login", FORA do "flow") p/ acompanhar quando
 * alguém em webview cai na tela de login — pra analisar problemas, sem poluir a conversão.
 */
export function logWebviewLogin(): void {
  if (typeof window === 'undefined') return;
  try {
    Sentry.logger.info('webview_login', {
      tipo: getInAppBrowserType() ?? 'desconhecido',
      ua: navigator.userAgent.slice(0, 180),
    });
  } catch {
    // ignora
  }
}

/**
 * Tipos de eventos de observabilidade
 */
export type ObservabilityEvent =
  | 'user_login'
  | 'user_logout'
  | 'list_created'
  | 'list_viewed'
  | 'list_shared'
  | 'item_added'
  | 'item_removed'
  | 'item_reserved'
  | 'checkout_started'
  | 'checkout_completed'
  | 'page_error'
  | 'api_error'
  | 'navigation';

export interface ObservabilityContext {
  userEmail?: string;
  listId?: string;
  listTitle?: string;
  itemId?: string;
  itemTitle?: string;
  browser?: string;
  isInstagram?: boolean;
  isFacebook?: boolean;
  [key: string]: unknown;
}

type MetricAttributeValue = string | number | boolean;

const MAX_METRIC_ATTRIBUTES = 40;
const MAX_STRING_VALUE = 180;

function normalizeMetricName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_.-]/g, '_')
    .replace(/_{2,}/g, '_')
    .replace(/^_+|_+$/g, '');
}

function toMetricAttributeValue(value: unknown): MetricAttributeValue | undefined {
  if (typeof value === 'string') {
    return value.slice(0, MAX_STRING_VALUE);
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'boolean') {
    return value;
  }
  if (value instanceof Date) {
    return value.toISOString().slice(0, MAX_STRING_VALUE);
  }
  if (value === null || value === undefined) {
    return undefined;
  }

  try {
    return JSON.stringify(value).slice(0, MAX_STRING_VALUE);
  } catch {
    return String(value).slice(0, MAX_STRING_VALUE);
  }
}

function toMetricAttributes(context: ObservabilityContext): Record<string, MetricAttributeValue> {
  const attributes: Record<string, MetricAttributeValue> = {};
  const entries = Object.entries(context).slice(0, MAX_METRIC_ATTRIBUTES);

  for (const [rawKey, rawValue] of entries) {
    const key = normalizeMetricName(rawKey);
    if (!key) continue;
    const value = toMetricAttributeValue(rawValue);
    if (value !== undefined) {
      attributes[key] = value;
    }
  }

  return attributes;
}

function enrichContext(context: ObservabilityContext = {}): ObservabilityContext {
  const browser =
    context.browser ||
    (isInstagramWebView() ? 'instagram' : isFacebookWebView() ? 'facebook' : 'browser');
  const isInstagram = isInstagramWebView();
  const isFacebook = isFacebookWebView();

  return {
    ...context,
    browser,
    isInstagram,
    isFacebook,
    site: process.env.REACT_APP_SITE_NAME || 'unknown',
    environment: process.env.NODE_ENV || 'development',
    inAppBrowser: isInstagram || isFacebook,
    timestamp: new Date().toISOString(),
  };
}

export function trackMetricCount(metricName: string, context: ObservabilityContext = {}): void {
  if (typeof window === 'undefined') return;

  const enrichedContext = enrichContext(context);
  const metric = normalizeMetricName(metricName);
  if (!metric) return;

  Sentry.metrics.count(metric, 1, {
    attributes: toMetricAttributes(enrichedContext),
  });
}

export function trackMetricDuration(
  metricName: string,
  durationMs: number,
  context: ObservabilityContext = {}
): void {
  if (typeof window === 'undefined' || !Number.isFinite(durationMs)) return;

  const enrichedContext = enrichContext(context);
  const metric = normalizeMetricName(metricName);
  if (!metric) return;

  Sentry.metrics.distribution(metric, durationMs, {
    unit: 'millisecond',
    attributes: toMetricAttributes(enrichedContext),
  });
}

export function trackLogInfo(message: string, context: ObservabilityContext = {}): void {
  if (typeof window === 'undefined') return;
  const enrichedContext = enrichContext(context);
  Sentry.logger.info(message, enrichedContext);
}

export function trackLogWarn(message: string, context: ObservabilityContext = {}): void {
  if (typeof window === 'undefined') return;
  const enrichedContext = enrichContext(context);
  Sentry.logger.warn(message, enrichedContext);
}

/**
 * Log enxuto para análise rápida no Sentry (inclusive no celular).
 * O `message` é fixo ("flow", campo obrigatório). Os dados vão em props pra você
 * adicionar como COLUNAS: `Flow` = ação (Criar | Presentear | Comprar | Oferta),
 * `flowDetail` = detalhe (tipo da lista, slug, valor...) e `extra` (ex.: { Lista: slug }).
 */
export function logM(
  flow: string,
  flowDetail: string | number,
  extra?: Record<string, string | number>
): void {
  if (typeof window === 'undefined') return;
  try {
    const attrs: Record<string, string> = { Flow: flow, flowDetail: String(flowDetail) };
    if (extra) {
      for (const [chave, valor] of Object.entries(extra)) attrs[chave] = String(valor);
    }
    Sentry.logger.info('flow', attrs);
  } catch {
    // ignora
  }
}

/**
 * Log de uso de filtro nas Ideias (message 'filtro', FORA do "flow" — não é conversão).
 * `flowDetail` = "tipo-valor" (ex.: "categoria-beleza", "loja-magalu", "preco-ate-50").
 */
export function logFiltro(tipoValor: string): void {
  if (typeof window === 'undefined') return;
  try {
    Sentry.logger.info('filtro', { flowDetail: tipoValor });
  } catch {
    // ignora
  }
}

/**
 * Registra eventos estruturados para observabilidade
 * Envia para Sentry com contexto de Instagram/Facebook
 */
export function recordEvent(
  eventName: ObservabilityEvent,
  context: ObservabilityContext = {}
): void {
  const enrichedContext = enrichContext({
    ...context,
    eventType: eventName,
  });

  trackMetricCount(`funnel.${eventName}`, enrichedContext);

  // Log estruturado no console (dev)
  if (process.env.NODE_ENV === 'development') {
    console.log(`[${eventName}]`, enrichedContext);
  }
}

/**
 * Registra erros com contexto de Instagram/Facebook
 */
export function recordError(error: Error | string, context: ObservabilityContext = {}): void {
  if (typeof window === 'undefined') return;

  const enrichedContext = enrichContext(context);

  const err = typeof error === 'string' ? new Error(error) : error;

  trackMetricCount('funnel.error', {
    ...enrichedContext,
    errorName: err.name,
  });

  Sentry.logger.error('flow.error', {
    ...enrichedContext,
    errorName: err.name,
    errorMessage: err.message,
  });

  Sentry.captureException(err, {
    tags: {
      browser: String(enrichedContext.browser || 'browser'),
      in_app_browser: enrichedContext.inAppBrowser ? 'true' : 'false',
    },
    extra: enrichedContext,
  });
}

/**
 * Registra tempo de operação (performance)
 * Útil para entender se há lentidão no Instagram vs browser normal
 */
export function recordTiming(
  operationName: string,
  duration: number,
  context: ObservabilityContext = {}
): void {
  const enrichedContext = enrichContext({
    ...context,
    operation: operationName,
  });

  trackMetricDuration(`perf.${operationName}`, duration, enrichedContext);

  if (process.env.NODE_ENV === 'development') {
    console.log(`[TIMING] ${operationName}: ${duration}ms (${enrichedContext.browser})`);
  }
}

/**
 * Hook para medir performance de uma função async
 */
export async function withTiming<T>(
  operationName: string,
  fn: () => Promise<T>,
  context?: ObservabilityContext
): Promise<T> {
  const startTime = performance.now();
  try {
    const result = await fn();
    const duration = Math.round(performance.now() - startTime);
    recordTiming(operationName, duration, context);
    return result;
  } catch (error) {
    const duration = Math.round(performance.now() - startTime);
    recordTiming(`${operationName} (ERROR)`, duration, context);
    throw error;
  }
}
